import React, { useEffect, useMemo, useRef } from "react";

import type * as Monaco from "monaco-editor";

import { Tooltip } from "@braze/beacon-core";
import { useTooltipOnTruncate } from "@braze/beacon-lab-hooks";

import {
  minChatInterfaceWidthPx,
  maxChatInterfaceWidthPx,
} from "@lib/AgenticAiChatInterfaceWidthContext/constants";
import { StackOrder } from "@lib/canvasStackOrder";

import { useAgenticAiChatInterfaceWidthContext } from "@services/useAgenticAiChatInterfaceWidthContext";
import useI18nNamespace from "@services/useI18nNamespace";

import { useLiquidSettingsContext } from "@components/Liquid/LiquidSettingsContext";
import { setupMonarchTokensProvider } from "@components/Liquid/helpers";
import { LIQUID_THEME } from "@components/MonacoHtmlEditor/config/constants";
import { monacoHtmlEditorShapeFromContext } from "@components/MonacoHtmlEditor/config/monacoHtmlEditorShapeFromContext";
import { useMonaco } from "@components/MonacoHtmlEditor/config/monacoLoader";
import { useLanguageSettings } from "@components/MonacoHtmlEditor/config/useLanguageSettings";
import { useLiquidThemeConfig } from "@components/MonacoHtmlEditor/config/useLiquidThemeConfig";

import { widthTransitionDurationMs } from "../AgenticChatInterface/AgenticChatInterface.style";
import {
  DiffDisplayContainer,
  DiffDisplayTitle,
  DiffEditorLabel,
  DiffTextContent,
  DiffTextPlaceholder,
} from "./DiffDisplay.style";
import { heightTransitionDurationMs, StyledDiffHighlightEditor } from "./DiffHighlightDisplay.style";
import { defaultHeightPx, diffHighlightEditorTestId, editorPaddingPx, maxHeightPx } from "./constants";
import type { DiffHighlightDisplayProps, InlineDecoration } from "./types";
import { computeDiff } from "./utils";

const editorOptions = {
  readOnly: true,
  automaticLayout: false,
  wordWrap: "on" as const,
  minimap: { enabled: false },
  renderOverviewRuler: false,
  scrollBeyondLastLine: false,
  scrollbar: {
    horizontal: "hidden" as const,
    verticalScrollbarSize: 16,
  },
  folding: false,
  glyphMargin: false,
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 3,
  stickyScroll: { enabled: false },
  // Vertical padding lives here rather than in CSS so that it is part of
  // getContentHeight(), which drives the height of the pane.
  padding: { top: editorPaddingPx, bottom: editorPaddingPx },
};

/**
 * Maps a filtered line back to its line number in the source text.
 * -1 means the line doesn't exist in the source (added/removed line).
 */
const createLineNumbersProvider = (lineNumbersMap: number[]) => (lineNumber: number) => {
  const mappedLineNum = lineNumbersMap[lineNumber - 1]; // Convert 1-based to 0-based index
  if (mappedLineNum === -1) {
    return "+"; // Line doesn't exist in source
  }
  return mappedLineNum !== undefined ? String(mappedLineNum) : String(lineNumber);
};

export const DiffHighlightDisplay = ({
  title,
  original = "",
  modified,
  language,
  placeholder,
}: DiffHighlightDisplayProps) => {
  const { t } = useI18nNamespace("agentic-diff-display");
  const originalRef = useRef<HTMLDivElement | null>(null);
  const modifiedRef = useRef<HTMLDivElement | null>(null);
  const monaco = useMonaco();
  const { width: parentWidth } = useAgenticAiChatInterfaceWidthContext();
  const liquidThemeConfig = useLiquidThemeConfig();
  const { getLanguageSettings } = useLanguageSettings();
  const liquidSettings = useLiquidSettingsContext();
  const insertionWithOptions = useMemo(() => monacoHtmlEditorShapeFromContext(liquidSettings), [liquidSettings]);
  const zIndex = StackOrder.OVERLAY_CONTENT_ABOVE_POPOVER;
  const [tooltipProps, targetRef] = useTooltipOnTruncate<HTMLSpanElement>();

  // A side with no content gets no editor: a first-time value shows on its own, and an
  // emptied value shows the placeholder — the same shapes DiffDisplay uses.
  const hasOriginal = Boolean(original);
  const hasModified = Boolean(modified);

  useEffect(() => {
    const originalContainer = hasOriginal ? originalRef.current : undefined;
    const modifiedContainer = hasModified ? modifiedRef.current : undefined;

    if (!monaco || (!originalContainer && !modifiedContainer)) {
      return;
    }

    setupMonarchTokensProvider({
      monaco,
      liquidThemeConfig,
      getLanguageSettings,
      language,
      insertionWithOptions,
    });
    monaco.editor.setTheme(LIQUID_THEME);

    // Compute diff with only changed lines. Only a change with both sides can be diffed;
    // a first-time or emptied value is rendered verbatim rather than as one solid block
    // of green or red.
    const diff =
      originalContainer && modifiedContainer ? computeDiff(original.split("\n"), modified.split("\n")) : undefined;
    const originalValue = diff ? diff.filteredOriginal : original;
    const modifiedValue = diff ? diff.filteredModified : modified;

    // Line numbers locate a change within a longer body. On a single line there is
    // nothing to locate, and a lone "1" in the gutter reads as content. The decision is
    // taken once so that both panes always agree.
    const isMultiline = originalValue.includes("\n") || modifiedValue.includes("\n");
    const lineNumbersFor = (
      lineNumbersMap: number[]
    ): Monaco.editor.IStandaloneEditorConstructionOptions["lineNumbers"] =>
      isMultiline ? createLineNumbersProvider(lineNumbersMap) : "off";

    // The editors are read-only widgets with no visible label of their own, so each one
    // carries the name of the side it shows for screen readers.
    const createPane = ({
      container,
      value,
      lineNumbersMap,
      ariaLabel,
    }: {
      container: HTMLDivElement | null | undefined;
      value: string;
      lineNumbersMap: number[];
      ariaLabel: string;
    }) => {
      if (!container) {
        return undefined;
      }
      const editor = monaco.editor.create(container, {
        ...editorOptions,
        value,
        language,
        ariaLabel,
        lineNumbers: lineNumbersFor(lineNumbersMap),
      });
      return { container, editor };
    };

    const originalPane = createPane({
      container: originalContainer,
      value: originalValue,
      lineNumbersMap: diff?.originalLineNumbers ?? [],
      ariaLabel: t("previous", "Previous"),
    });
    const modifiedPane = createPane({
      container: modifiedContainer,
      value: modifiedValue,
      lineNumbersMap: diff?.modifiedLineNumbers ?? [],
      ariaLabel: t("proposed", "Proposed"),
    });
    const panes = [originalPane, modifiedPane].filter((pane) => pane !== undefined);

    // Convert to Monaco decoration format and apply
    const toMonacoDecorations = (decorations: InlineDecoration[]): Monaco.editor.IModelDeltaDecoration[] =>
      decorations.map((d) => ({
        range: {
          startLineNumber: d.lineNumber,
          startColumn: d.startColumn,
          endLineNumber: d.lineNumber,
          endColumn: d.endColumn,
        },
        options: d.isWholeLine
          ? {
              isWholeLine: true,
              className: d.className,
              marginClassName: d.marginClassName,
            }
          : {
              inlineClassName: d.className,
            },
      }));

    if (diff) {
      originalPane?.editor.getModel()?.deltaDecorations([], toMonacoDecorations(diff.originalDecorations));
      modifiedPane?.editor.getModel()?.deltaDecorations([], toMonacoDecorations(diff.modifiedDecorations));
    }

    const disposables: Monaco.IDisposable[] = [];

    // Synchronize scrolling between the two editors
    if (originalPane && modifiedPane) {
      let isSyncing = false;

      disposables.push(
        originalPane.editor.onDidScrollChange((e) => {
          if (isSyncing) {
            return;
          }
          isSyncing = true;
          modifiedPane.editor.setScrollTop(e.scrollTop);
          isSyncing = false;
        })
      );

      disposables.push(
        modifiedPane.editor.onDidScrollChange((e) => {
          if (isSyncing) {
            return;
          }
          isSyncing = true;
          originalPane.editor.setScrollTop(e.scrollTop);
          isSyncing = false;
        })
      );
    }

    // If parent width is greater than halfway between min and max chat interface width,
    // panes may grow to max height. Otherwise they cap at the default height.
    const isWideEnough =
      parentWidth != null &&
      !Number.isNaN(parentWidth) &&
      parentWidth > (minChatInterfaceWidthPx + maxChatInterfaceWidthPx) / 2;
    const maxPaneHeightPx = isWideEnough ? maxHeightPx : defaultHeightPx;

    // Panes grow with their content up to that ceiling, and share the taller of the two
    // heights so that they stay aligned. A one-line change renders as a one-line box.
    //
    // Width is applied before the content is measured: soft wrapping decides how many
    // rows the content occupies, so measuring first would size the pane to the wrapping
    // of the previous width. The content-size change that the re-wrap emits is swallowed
    // by the re-entrancy guard, which is why it cannot be relied on to correct this.
    let isApplyingLayout = false;
    const applyLayout = () => {
      if (isApplyingLayout) {
        return;
      }
      isApplyingLayout = true;
      try {
        for (const { editor, container } of panes) {
          editor.layout({ width: container.clientWidth, height: container.clientHeight });
        }
        const contentHeightPx = Math.max(...panes.map(({ editor }) => editor.getContentHeight()));
        const paneHeightPx = Math.min(contentHeightPx, maxPaneHeightPx);
        for (const { editor, container } of panes) {
          container.style.height = `${paneHeightPx}px`;
          container.style.minHeight = `${paneHeightPx}px`;
          editor.layout({ width: container.clientWidth, height: paneHeightPx });
        }
      } finally {
        isApplyingLayout = false;
      }
    };

    // Soft-wrapped lines change the content height whenever the pane width changes.
    for (const { editor } of panes) {
      disposables.push(editor.onDidContentSizeChange(applyLayout));
    }

    // Re-measure once the chat panel's width and height transitions have settled.
    const timeout = setTimeout(applyLayout, heightTransitionDurationMs + widthTransitionDurationMs);

    applyLayout();
    window.addEventListener("resize", applyLayout);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", applyLayout);
      for (const disposable of disposables) {
        disposable.dispose();
      }
      for (const { editor } of panes) {
        editor.dispose();
      }
    };
  }, [
    monaco,
    hasOriginal,
    hasModified,
    original,
    modified,
    language,
    liquidThemeConfig,
    getLanguageSettings,
    insertionWithOptions,
    parentWidth,
    t,
  ]);

  // Monaco cannot render placeholder text, so an empty value falls back to the same
  // markup DiffDisplay uses for one.
  const proposedValue = hasModified ? (
    <StyledDiffHighlightEditor ref={modifiedRef} data-testid={diffHighlightEditorTestId} />
  ) : (
    <DiffTextContent>
      <DiffTextPlaceholder>{placeholder ?? t("none-placeholder", "None")}</DiffTextPlaceholder>
    </DiffTextContent>
  );

  const cardTitle = (
    <>
      <DiffDisplayTitle ref={targetRef}>{title}</DiffDisplayTitle>
      <Tooltip {...tooltipProps} targetRef={targetRef} zIndex={zIndex}>
        {title}
      </Tooltip>
    </>
  );

  if (!hasOriginal) {
    return (
      <DiffDisplayContainer>
        {cardTitle}
        {proposedValue}
      </DiffDisplayContainer>
    );
  }

  return (
    <DiffDisplayContainer>
      {cardTitle}
      <div>
        <DiffEditorLabel>{t("previous", "Previous")}</DiffEditorLabel>
        <StyledDiffHighlightEditor ref={originalRef} data-testid={diffHighlightEditorTestId} />
      </div>
      <div>
        <DiffEditorLabel>{t("proposed", "Proposed")}</DiffEditorLabel>
        {proposedValue}
      </div>
    </DiffDisplayContainer>
  );
};
